import { ChangeDetectionStrategy, Component, OnDestroy, OnInit, signal } from '@angular/core';
import { Observable, of, Subscription, first, switchMap, take } from 'rxjs';

import { ProductService } from '@spartacus/core';

import {
  BrandsEnum, DeviceService, IExtendedLink,
  ILinkMedia, SdSpyTargetDirective, IPushCardTracking
} from '@seb/web-design-system/core';
import {
  IPushCardBanner, IPushCardBannerTracking, SdPushCardBannerComponent
} from '@seb/web-design-system/push-card-banner';

import {
  CmsModuleComponent, CustomMedia, ExtendedIMedia, PushCardBannerModuleInterface,
  PushCardComponentInterface, miniPushCardSkeleton, pushCardWithLayoutSkeleton
} from '../../../models';

import { PUSH_CARD_CONSTANTS } from './push-card-banner-module.constants';
import { AbstractModuleComponent } from '../abstract-module.component';
import { ChatService, ImageService, TrackingService } from '../../../services';
import { ProductScopeEnum } from '../../../enums';
import { PushCardMapper } from '../../../mappers';

const MAIN_PUSH_CARD_POSITION = 1;

interface TrackingContext {
  position: number;
  productCode?: string;
  productName?: string;
}

@Component({
  selector: 'wc-push-card-banner-module',
  templateUrl: './push-card-banner-module.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    SdPushCardBannerComponent,
    SdSpyTargetDirective
  ]
})
export class PushCardBannerModuleComponent extends AbstractModuleComponent<PushCardBannerModuleInterface> implements OnInit, OnDestroy {
  skeleton: IPushCardBanner;

  moduleName: string;
  data = signal<IPushCardBanner>({
    push: null,
    miniPush: []
  });
  trackingData = signal<IPushCardBannerTracking>({
    push: null,
    miniPush: []
  });

  private readonly MAX_PUSH_CARDS = PUSH_CARD_CONSTANTS.MAX_PUSH_CARDS;
  private numberOfMiniCards: number;
  private numberOfLoadedMiniCards: number;
  private mainPushLoaded = false;
  private mediaSubscriptions: Subscription[] = [];
  private mediaMainSubscription: Subscription;
  private readonly isMobile = signal<boolean>(false);
  private mobileSubscription: Subscription;

  constructor(
    private readonly pushCardMapper: PushCardMapper,
    private readonly imageService: ImageService,
    private readonly productService: ProductService,
    private readonly chatService: ChatService,
    private readonly deviceService: DeviceService,
    private readonly trackingService: TrackingService
  ) {
    super();
  }

  override mandatoryComponentFunction = (cmp: PushCardComponentInterface): boolean => {
    return !!cmp.media || !!cmp.mediaContainer;
  };

  override onModuleInit(_module: CmsModuleComponent) {
    if (this.useLazyLoad) {
      this.skeleton = {
        push: pushCardWithLayoutSkeleton,
        miniPush: [miniPushCardSkeleton, miniPushCardSkeleton]
      };
      this.data.set(this.skeleton);
    }
    else {
      this.data.update((data) => ({ ...data, miniPush: [] }));
    }
  }

  ngOnInit(): void {
    this.mobileSubscription = this.deviceService.isMobile.subscribe((isMobile) => {
      this.isMobile.set(isMobile);
    });
    this.subscriptions.add(this.mobileSubscription);
    this.subscriptions.add(
      this.getModuleWithLazyData().subscribe(
        (module: PushCardBannerModuleInterface) => this.handleModuleData(module)
      )
    );
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
    this.destroy$.next();
    this.destroy$.complete();
    this.mediaSubscriptions.forEach((sub) => sub?.unsubscribe());
    this.mobileSubscription.unsubscribe();
  }

  private getModuleWithLazyData(): Observable<CmsModuleComponent> {
    return this.module$.pipe(
      switchMap((module: PushCardBannerModuleInterface) =>
        this.componentService.getSubComponentsLazyComponentData(module, 'componentsObjects', 'links')
      ),
      switchMap((module: PushCardBannerModuleInterface) =>
        this.componentService.getSubComponentsLazyComponentData(module, 'componentsObjects', 'tags')
      ),
      take(1)
    );
  }

  private handleModuleData(module: PushCardBannerModuleInterface): void {
    this.moduleName = module?.name || '';
    const { componentsObjects } = module;
    const sortedComponents = [...componentsObjects].sort((a, b) => {
      const positionA = a.orderIndex ?? Number.MAX_SAFE_INTEGER;
      const positionB = b.orderIndex ?? Number.MAX_SAFE_INTEGER;
      return positionA - positionB;
    });
    const safeMaxCards = Math.max(0, Math.min(this.MAX_PUSH_CARDS, sortedComponents.length));
    const pushCardsComponents = sortedComponents.slice(0, safeMaxCards);
    const [mainCard, ...miniCards] = pushCardsComponents;

    this.initializePushCards(pushCardsComponents);
    this.handleMainPushCard(mainCard);
    this.handleMiniPushCards(miniCards);
  }

  private initializePushCards(pushCardsComponents: PushCardComponentInterface[]): void {
    this.numberOfMiniCards = pushCardsComponents.length - 1;
    const currentData = this.data();
    const updatedMiniPush = [...currentData.miniPush];
    updatedMiniPush.splice(this.numberOfMiniCards);
    this.data.update((data) => ({ ...data, miniPush: updatedMiniPush }));
    this.numberOfLoadedMiniCards = 0;

    if (pushCardsComponents.length <= 1) {
      this.data.update((data) => ({ ...data, miniPush: [] }));
      this.setLoaded();
    }
  }

  private handleMainPushCard(cmp: PushCardComponentInterface): void {
    if (cmp?.mediaContainer) {
      this.handleComponentWithMedia(cmp, undefined, this.updateMainPushCard.bind(this, cmp));
    } else {
      this.updateMainPushCard(cmp);
    }
  }

  private handleMiniPushCards(components: PushCardComponentInterface[]): void {
    components.forEach((cmp, index) => {
      if (cmp?.mediaContainer) {
        this.handleComponentWithMedia(cmp, index, this.updateMiniPushCard.bind(this));
      } else {
        this.updateMiniPushCard(cmp, index);
      }
    });
  }

  private handleComponentWithMedia(
    cmp: PushCardComponentInterface,
    index: number | undefined,
    updateFn: (cmp: PushCardComponentInterface, index?: number, media?: CustomMedia) => void
  ): void {
    const subscription = this.imageService.getMediaFromContainer(cmp.mediaContainer).subscribe((media) => {
      updateFn(cmp, index, media);
    });

    if (index !== undefined) {
      this.mediaSubscriptions[index]?.unsubscribe();
      this.mediaSubscriptions[index] = subscription;
    }
    else {
      this.mediaMainSubscription?.unsubscribe();
      this.mediaMainSubscription = subscription;
    }
  }

  private updateMainPushCard(cmp: PushCardComponentInterface, media?: CustomMedia): void {
    const component = this.prepareComponentWithBrand(cmp);
    const normalizedMedia = media ? this.imageService.normalizeMedia(media) : undefined;

    if (this.isNonProductLink(component)) {
      this.handleNonProductPushCard(component, normalizedMedia);
      return;
    }

    this.handleProductPushCard(component, normalizedMedia);
  }

  private prepareComponentWithBrand(cmp: PushCardComponentInterface): PushCardComponentInterface {
    const brand = this.baseStoreService.getBrand(cmp?.['brandCorner']);
    const brandCorner = brand ? brand.id as BrandsEnum : BrandsEnum.default;
    return { ...cmp, brandCorner };
  }

  private isNonProductLink(cmp: PushCardComponentInterface): boolean {
    return !cmp?.linksObjects?.[0]?.urlLocalized?.includes('/p/');
  }

  private handleNonProductPushCard(cmp: PushCardComponentInterface, media?: ExtendedIMedia): void {
    this.setMainPushCard(cmp, media, { position: MAIN_PUSH_CARD_POSITION });
  }

  private handleProductPushCard(cmp: PushCardComponentInterface, media?: ExtendedIMedia): void {
    const productCode = cmp?.linksObjects?.[0]?.urlLocalized?.split('/p/')[1] || '';
    this.subscriptions.add(
      this.getProduct(productCode).subscribe((product) => {
        const updatedComponent = this.addProductData(cmp, product);
        const tracking: TrackingContext = {
          position: MAIN_PUSH_CARD_POSITION,
          productCode: product?.code,
          productName: product?.name
        };
        this.setMainPushCard(updatedComponent, media, tracking);
      })
    );
  }

  private getProduct(productCode: string): Observable<any> {
    return this.productService.hasError(productCode, ProductScopeEnum.CARD).pipe(
      switchMap((hasError) => {
        if (hasError) {
          return of(null);
        }
        return productCode
          ? this.productService.get(productCode, ProductScopeEnum.CARD).pipe(first((p) => !!p))
          : of(null);
      }),
      take(1)
    );
  }

  private setMainPushCard(cmp: PushCardComponentInterface, media?: ExtendedIMedia, tracking?: TrackingContext): void {
    this.data.update((data) => ({
      ...data,
      push: this.pushCardMapper.toPushCardWithLayout(cmp, media, this.isMobile())
    }));
    this.trackingData.update((trackingData) => ({
      ...trackingData,
      push: this.pushCardMapper.createTrackingData(
        cmp,
        tracking.position,
        this.moduleName,
        tracking.productCode,
        tracking.productName
      )
    }));
    this.setPushHeadingInverted(true);
    this.mainPushLoaded = true;
    this.checkAndSetLoaded();
  }

  private updateMiniPushCard(cmp: PushCardComponentInterface, index?: number, media?: CustomMedia): void {
    const normalizedMedia = media ? this.imageService.normalizeMedia(media) : undefined;
    const position = MAIN_PUSH_CARD_POSITION + (index || 0) + 1;

    if (this.isNonProductLink(cmp)) {
      this.setMiniPushCard(cmp, index, normalizedMedia, { position });
      return;
    }

    const productCode = cmp?.linksObjects?.[0]?.urlLocalized?.split('/p/')[1] || '';
    this.subscriptions.add(
      this.getProduct(productCode).subscribe((product) => {
        const tracking: TrackingContext = {
          position,
          productCode: product?.code,
          productName: product?.name
        };
        this.setMiniPushCard(cmp, index, normalizedMedia, tracking);
      })
    );
  }

  private setMiniPushCard(cmp: PushCardComponentInterface, index: number, media?: ExtendedIMedia, tracking?: TrackingContext) {
    this.data.update((data) => {
      const updatedMiniPush = [...data.miniPush];
      updatedMiniPush[index] = this.pushCardMapper.toMiniPushCard(cmp, media, this.isMobile());
      return { ...data, miniPush: updatedMiniPush };
    });
    this.trackingData.update((trackingData) => {
      const updatedMiniPush = [...trackingData.miniPush];
      updatedMiniPush[index] = this.pushCardMapper.createTrackingData(
        cmp,
        tracking.position,
        this.moduleName,
        tracking.productCode,
        tracking.productName
      );
      return { ...trackingData, miniPush: updatedMiniPush };
    });
    this.setPushHeadingInverted(true, index);
    this.numberOfLoadedMiniCards++;
    this.checkAndSetLoaded();
  }

  private checkAndSetLoaded(): void {
    if (this.mainPushLoaded && this.numberOfLoadedMiniCards === this.numberOfMiniCards) {
      this.setLoaded();
    }
  }

  private addProductData(cmp: PushCardComponentInterface, product: any): PushCardComponentInterface {
    if (!cmp.linksObjects?.[0]?.urlLocalized?.includes('/p/') || !product) {
      return cmp;
    }

    const updatedCmp = { ...cmp };
    if (product && updatedCmp.linksObjects?.length > 0) {
      updatedCmp.linksObjects[0].urlLocalized = product.url;
    }

    if (cmp.linksObjects[0].urlLocalized.startsWith('/p/')) {
      updatedCmp.linksObjects = cmp.linksObjects[0].urlLocalized.endsWith('/p/') ?
        null :
        updatedCmp.linksObjects;
    }

    updatedCmp.indexFollow = product.indexFollow;
    return updatedCmp;
  }

  private setPushHeadingInverted(value: boolean, index?: number): void {
    const data = this.data();
    const heading = index !== undefined
      ? data?.miniPush?.[index]?.richParagraph?.heading // Mini push
      : data?.push?.push?.richParagraph?.heading; // Main push

    if (heading) {
      heading.isInverted = value;
    }
  }

  onClick(event: IExtendedLink | ILinkMedia): void {
    if (event?.url === '<<chat>>') {
      this.chatService.clickOpenChat();
    }
  }

  onCarouselClicked(index?: number) {
    const trackingData: IPushCardTracking = Number.isInteger(index)
      ? this.trackingData()?.miniPush?.[index]
      : this.trackingData()?.push;
    this.trackingService.sendEvent('carouselClick', trackingData);
  }
}
